import { GeometryResponse, type RouteLinePoint, type RoutePoint } from '@xingroute/schema'
import { trackBounds, trackStats } from './gpx.js'
import { snapWithinThreshold } from './snapToTrack.js'

export type ProductGeometry = GeometryResponse & {
  geometry_type: 'line'
  line: RouteLinePoint[]
  points: RoutePoint[]
  track: RouteLinePoint[]
  waypoints: RoutePoint[]
}

export function normalizeGeometry(geometry: GeometryResponse | unknown): ProductGeometry {
  const parsed = GeometryResponse.parse(geometry)
  return {
    ...parsed,
    geometry_type: 'line',
    track: parsed.line,
    waypoints: parsed.points,
  }
}

export function lineDistanceKm(line: RouteLinePoint[]): number {
  return trackStats(line.map((point) => ({ lat: point.lat, lon: point.lon, ele: point.ele })))
    .distanceKm
}

export function snapPointToLine(
  point: { lat: number; lon: number },
  line: RouteLinePoint[],
  thresholdM = 5,
): {
  lat: number
  lon: number
  ele?: number
  distanceM: number
  distanceAlongLineKm: number
} | null {
  const snapped = snapWithinThreshold(point.lat, point.lon, line, thresholdM)
  if (!snapped) return null
  let distanceM = 0
  let bestAlongM = 0
  let bestDistanceM = Infinity
  for (let i = 1; i < line.length; i += 1) {
    const a = line[i - 1]!
    const b = line[i]!
    const segmentM = segmentMeters(a, b)
    const projected = projectPointToSegment(point, a, b)
    const alongM = distanceM + segmentM * projected.t
    if (projected.distanceM < bestDistanceM) {
      bestDistanceM = projected.distanceM
      bestAlongM = alongM
    }
    distanceM += segmentM
  }
  return {
    lat: snapped.lat,
    lon: snapped.lon,
    ele: snapped.ele,
    distanceM: snapped.distanceM,
    distanceAlongLineKm: bestAlongM / 1000,
  }
}

export function boundsForGeometry(geometry: GeometryResponse): {
  minLon: number
  minLat: number
  maxLon: number
  maxLat: number
  center: { lon: number; lat: number }
} | null {
  const normalized = normalizeGeometry(geometry)
  const coords = normalized.line.length > 0 ? normalized.line : normalized.points
  const bounds = trackBounds(coords)
  if (!bounds) return null
  const [[minLon, minLat], [maxLon, maxLat]] = bounds
  return {
    minLon,
    minLat,
    maxLon,
    maxLat,
    center: { lon: (minLon + maxLon) / 2, lat: (minLat + maxLat) / 2 },
  }
}

function segmentMeters(a: RouteLinePoint, b: RouteLinePoint): number {
  const toRad = (value: number) => (value * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLon = toRad(b.lon - a.lon)
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2
  return 6_371_000 * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s))
}

function projectPointToSegment(
  point: { lat: number; lon: number },
  a: RouteLinePoint,
  b: RouteLinePoint,
): { distanceM: number; t: number } {
  const meanLat = ((a.lat + b.lat) / 2 / 180) * Math.PI
  const ax = a.lon * Math.cos(meanLat)
  const ay = a.lat
  const bx = b.lon * Math.cos(meanLat)
  const by = b.lat
  const px = point.lon * Math.cos(meanLat)
  const py = point.lat
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  const t = len2 > 0 ? Math.min(1, Math.max(0, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0
  const lon = a.lon + (b.lon - a.lon) * t
  const lat = a.lat + (b.lat - a.lat) * t
  return { distanceM: segmentMeters(point, { lat, lon }), t }
}
